<template>
  <div class="site-footer">
    <div class="site-footer__logo">
      <Logo />
    </div>
    <div class="site-footer__contents site-footer__constants--method">
      <h2 class="site-footer__heading">探し方</h2>
      <ul class="site-footer__links">
        <li class="site-footer__link">
          <nuxt-link to="/">キーワードから探す</nuxt-link>
        </li>
        <li class="site-footer__link">
          <a href="/labs/wandh">リサーチAI “Watson & Holmes β版”</a>
        </li>
        <li v-if="showPracticeArea" class="site-footer__link">
          <nuxt-link to="/directories">分野別ニュース</nuxt-link>
        </li>
        <li v-if="showMyCollection" class="site-footer__link">
          <nuxt-link to="/collections">マイ・コレクション</nuxt-link>
        </li>
        <li class="site-footer__link">
          <nuxt-link to="/recently-added">新着書籍</nuxt-link>
        </li>
      </ul>
    </div>
    <div class="site-footer__contents site-footer__constants--about">
      <h2 class="site-footer__heading">Legalscapeについて</h2>
      <ul class="site-footer__links">
        <li v-if="manualLink" class="site-footer__link">
          <a :href="manualLink" target="_blank">マニュアル・使い方が分からないときは</a>
        </li>
        <li v-if="termsURL" class="site-footer__link">
          <a :href="termsURL" target="_blank">利用規約</a>
        </li>
        <li v-if="apiTerms" class="site-footer__link">
          <a :href="apiTerms" target="_blank">外部連携規約</a>
        </li>
        <li v-if="privacyPolicy" class="site-footer__link">
          <a :href="privacyPolicy" target="_blank">プライバシーポリシー</a>
        </li>
        <li class="site-footer__link">
          <a href="https://www.legalscape.co.jp/" target="_blank">運営会社</a>
        </li>
        <li class="site-footer__link">
          <a href="https://legalscape.github.io/wklr/licenses" target="_blank">ライセンス情報</a>
        </li>
        <li v-if="user.privilege === 'full' && permissions.orgAdmin" class="site-footer__link">
          <a href="/admin">管理画面</a>
        </li>
      </ul>
    </div>
    <div class="site-footer__version">{{ versionInfo }}</div>
    <div class="site-footer__copyright">&copy;2017–{{ year }} Legalscape, Inc.</div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useAuth } from '@/plugins/auth';
import { useDomain } from '@/plugins/domain';
import { AboutLegalscape } from '@/constants';
import { getManualURL } from '@/utils/manual';
import type { PluginExtension } from '@/types/pluginExtension';
import Logo from '@/components/renewal/common/logo.vue';

/** バージョン情報（バグ報告用） */
const versionInfo = process.env.versionInfo;

const year = new Date().getFullYear();
const auth = useAuth();
const domain = useDomain();
const { permissions, user } = auth;

const showPracticeArea = computed(() => permissions.practiceArea);

const showMyCollection = computed(() => permissions.quickAccess);

const organizationConfig = computed(() => user.organization.config);

const manualLink = getManualURL({ $domain: domain, $auth: auth } as PluginExtension);
const termsURL = computed(() => {
  if (organizationConfig.value?.disableTermsOfService) {
    return null;
  } else if (organizationConfig.value?.overrideTermsOfServiceUrl) {
    return organizationConfig.value.overrideTermsOfServiceUrl;
  } else if (user.organization.sellerId === 2) {
    // LegalOn 販売の場合は「Legalscape 追加オプション利用規約」を表示する
    return AboutLegalscape.TERMS_OF_SERVICE_VIA_LEGALON;
  } else {
    return AboutLegalscape.TERMS_OF_SERVICE_GENERAL;
  }
});
const apiTerms = computed(() => {
  if (organizationConfig.value?.disableTermsOfService) {
    return null;
  } else {
    return AboutLegalscape.API_TERMS;
  }
});
const privacyPolicy = computed(() => {
  if (organizationConfig.value?.disablePrivacyPolicy) {
    return null;
  } else {
    return AboutLegalscape.PRIVACY_POLICY;
  }
});
</script>

<style lang="scss" scoped src="./site-footer.scss"></style>
